import IconX from "@src/assets/icons/tabler/x.svg?react";
import { LogoutButton } from "@src/components/buttons/logout-button";
import { PlanFamily, PlanStatus } from "@src/gql/graphql";
import { useState } from "react";
import { useMyPlanSuspense } from "../queries/fetch-plan";
import { useProductInformationQuery } from "../queries/product-information-query";
import { CustomisePlanCard } from "./cards/plan-modal-card-customise";
import { FreePlanCard } from "./cards/plan-modal-card-free-annual";
import { ProPlanCard } from "./cards/plan-modal-card-pro";
import { TeamPlanCard } from "./cards/plan-modal-card-team";
import { PlanModalHeader } from "./plan-modal-header";
import { PlanPeriodToggle } from "./plan-period-toggle";

export function PlanModalContent({
	onClose,
	dismissible = true,
}: {
	onClose: () => void;
	dismissible?: boolean;
}) {
	const { data: plan } = useMyPlanSuspense();
	const { data: productInfo } = useProductInformationQuery();

	const { proMonthly, proAnnual, starterAnnual } = productInfo.priceIds;

	const [periodSelected, setPeriodSelected] = useState<"monthly" | "annually">(
		"monthly",
	);

	const starterAnnualAvailable = starterAnnual !== null;
	const proAnnualAvailable = proAnnual !== null;
	const showAnnuallySwitch = starterAnnualAvailable || proAnnualAvailable;
	const isLogoutAvailable =
		plan.status === PlanStatus.TrialEnd ||
		plan.status === PlanStatus.Expired ||
		plan.status === PlanStatus.Paused;
	const isPro = plan.planFamily === PlanFamily.Pro;
	const isStarter = plan.planFamily === PlanFamily.Starter;

	const starterLabel =
		isPro &&
		(plan.status === PlanStatus.TrialEnd ||
			plan.status === PlanStatus.Expired ||
			plan.status === PlanStatus.Cancelled)
			? "Downgrade"
			: isStarter && plan.status === PlanStatus.Active
				? "Current Plan"
				: "Get Started";

	const showStarterCta = !(isPro && plan.status === PlanStatus.Active);

	const proLabel =
		isPro && plan.status === PlanStatus.TrialEnd
			? "Upgrade"
			: isPro && plan.status === PlanStatus.Active
				? "Current Plan"
				: isPro && plan.status === PlanStatus.Cancelled
					? "Renew"
					: "Upgrade";

	return (
		<div className="relative p-6" onClick={(e) => e.stopPropagation()}>
			{dismissible && (
				<button
					type="button"
					onClick={onClose}
					className="sm:hidden absolute top-3 right-3 z-20 h-8 w-8 rounded-full bg-surface-subtle backdrop-blur shadow-md hover:bg-surface-hover-subtle flex items-center justify-center"
					aria-label="Close upgrade popup"
				>
					<IconX className="w-4 h-4 text-text-secondary" />
				</button>
			)}
			<div className="flex flex-col gap-2 mb-8 text-center">
				<PlanModalHeader
					planFamily={plan.planFamily}
					status={plan.status}
					trialEnd={plan.trialEnd}
				/>
			</div>
			{showAnnuallySwitch && (
				<PlanPeriodToggle
					periodSelected={periodSelected}
					onPeriodChange={setPeriodSelected}
				/>
			)}
			<div className="grid grid-cols-1 gap-5 max-w-6xl mx-auto md:grid-cols-4">
				<FreePlanCard
					isPlanActive={isStarter && plan.status === PlanStatus.Active}
					isButtonDisabled={
						plan.status !== PlanStatus.TrialEnd &&
						plan.status !== PlanStatus.Expired
					}
					isDowngrade={
						isPro &&
						(plan.status === PlanStatus.TrialEnd ||
							plan.status === PlanStatus.Expired)
					}
					isInTrial={plan.status === PlanStatus.InTrial}
					isTrialEnded={plan.status === PlanStatus.TrialEnd}
					proCheckoutPriceId={proMonthly}
					label={starterLabel}
					showCta={showStarterCta}
					onDowngradeSuccess={onClose}
				/>

				<ProPlanCard
					isPlanActive={
						isPro &&
						(plan.status === PlanStatus.Active ||
							plan.status === PlanStatus.Cancelled)
					}
					isPlanCancelled={plan.status === PlanStatus.Cancelled}
					isButtonDisabled={plan.status === PlanStatus.Paused}
					periodSelected={periodSelected}
					proMonthlyPriceId={proMonthly}
					proAnnualPriceId={proAnnual}
					label={proLabel}
				/>

				<TeamPlanCard periodSelected={periodSelected} />

				<CustomisePlanCard
					isButtonDisabled={plan.status === PlanStatus.Paused}
				/>
			</div>
			{isLogoutAvailable && (
				<div className="flex justify-center mt-10">
					<LogoutButton className="rounded-xl px-6 h-10 text-xs bg-surface-subtle hover:bg-surface-hover-subtle text-text-secondary border-none font-semibold transition-all" />
				</div>
			)}
		</div>
	);
}
